import { FormEvent, useState } from "react";
import { supabase } from "../lib/supabase";
import type { HistoricalPriceGroup } from "../types";

const units = [
  ["", "Todas as unidades"],
  ["m2", "m²"],
  ["m3", "m³"],
  ["ml", "ml"],
  ["un", "un"],
  ["vg", "VG"],
  ["h", "h"],
  ["kg", "kg"],
];

function euro(value: number | string | null | undefined) {
  if (value === null || value === undefined || value === "") return "—";
  return new Intl.NumberFormat("pt-PT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value));
}

export default function SearchHistorical() {
  const [query, setQuery] = useState("");
  const [unit, setUnit] = useState("");
  const [results, setResults] = useState<HistoricalPriceGroup[]>([]);
  const [busy, setBusy] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState("");

  async function search(event?: FormEvent) {
    event?.preventDefault();
    const clean = query.trim();
    if (!clean) return;

    setBusy(true);
    setError("");
    setSearched(true);

    const { data, error: searchError } = await supabase.rpc(
      "search_source_price_groups",
      {
        search_query: clean,
        unit_filter: unit || null,
        result_limit: 12,
      },
    );

    if (searchError) {
      setError(searchError.message);
      setResults([]);
    } else {
      setResults((data ?? []) as HistoricalPriceGroup[]);
    }
    setBusy(false);
  }

  return (
    <section className="stack-xl">
      <header className="page-header">
        <div>
          <p className="eyebrow">Histórico L37</p>
          <h2>Pesquisar preço de referência</h2>
          <p className="muted">
            Os resultados são referências históricas de PU seco. Não são preços
            aprovados automaticamente para uma nova obra.
          </p>
        </div>
      </header>

      <form className="search-panel card" onSubmit={search}>
        <div className="search-main">
          <label className="grow">
            <span>Trabalho, material ou descrição</span>
            <input
              type="search"
              placeholder="Ex.: alvenaria tijolo 11 cm, ETICS, pintura exterior…"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>

          <label className="unit-field">
            <span>Unidade</span>
            <select value={unit} onChange={(event) => setUnit(event.target.value)}>
              {units.map(([value, label]) => (
                <option value={value} key={value || "all"}>
                  {label}
                </option>
              ))}
            </select>
          </label>

          <button className="button button-primary search-button" type="submit" disabled={busy}>
            {busy ? "A procurar…" : "Pesquisar"}
          </button>
        </div>
      </form>

      {error ? <div className="alert alert-error">{error}</div> : null}

      {!searched ? (
        <div className="empty card">
          <strong>Comece por uma palavra ou descrição.</strong>
          <span>
            Pode escrever apenas “alvenaria” ou colar parte de uma descrição de
            uma LPU. O filtro por unidade ajuda a eliminar referências irrelevantes.
          </span>
        </div>
      ) : null}

      {searched && !busy && !error && results.length === 0 ? (
        <div className="empty card">
          <strong>Sem referências encontradas.</strong>
          <span>Experimente reduzir a descrição ou retirar o filtro de unidade.</span>
        </div>
      ) : null}

      <div className="result-list">
        {results.map((result) => {
          const hasRange =
            Number(result.min_dry_unit_price) !== Number(result.max_dry_unit_price);

          return (
            <article className="price-result card" key={result.group_key}>
              <div className="result-copy">
                <div className="result-meta">
                  <span className="pill">{result.original_unit || "—"}</span>
                  <span>
                    {Number(result.observation_count).toLocaleString("pt-PT")} observ.
                  </span>
                  <span>
                    {Number(result.project_count).toLocaleString("pt-PT")} obra(s)
                  </span>
                  {result.latest_year ? <span>{result.latest_year}</span> : null}
                </div>

                <h3>{result.original_description}</h3>

                <div className="source-line">
                  <span className="muted">Exemplo mais recente:</span>
                  <span>{result.sample_project_name || result.sample_source_title || "—"}</span>
                </div>
              </div>

              <div className="price-block">
                <span className="price-label">PU seco mediano</span>
                <strong>{euro(result.median_dry_unit_price)}</strong>
                {hasRange ? (
                  <small>
                    intervalo {euro(result.min_dry_unit_price)} –{" "}
                    {euro(result.max_dry_unit_price)}
                  </small>
                ) : (
                  <small>valor consistente nas observações agrupadas</small>
                )}

                <div className="sample-price">
                  <span>Última amostra</span>
                  <b>{euro(result.sample_dry_unit_price)}</b>
                  {result.sample_commercial_coefficient ? (
                    <small>
                      coef. {Number(result.sample_commercial_coefficient).toLocaleString("pt-PT", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{" "}
                      · venda {euro(result.sample_sale_unit_price)}
                    </small>
                  ) : null}
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
